import { motion, AnimatePresence } from "framer-motion";
import Fetcher from "../hooks/Fetcher";
import { useState, useEffect } from "react";
import {
  CalendarDays,
  Clock,
  Laptop,
  MapPin,
  Award,
  ShieldCheck,
  Target,
  X,
  ExternalLink,
} from "lucide-react";

const url =
  "https://eventos.utpl.edu.ec/jsonapi/node/congresos/c03391f4-7827-4daa-9a89-8933405d5bdb" +
  "?include=field_portada.field_media_image" +
  "&fields[node--congresos]=title,field_ponente,field_texto_portada,field_portada,field_bloque1,field_bloque2,field_bloque3,field_bloque4,field_bloque5,field_bloque6," +
  "field_bloque7,field_bloque8,field_bloque9,field_bloque10,field_bloque11,field_bloque12,field_bloque13,field_bloque14,field_bloque15,field_bloque16,field_bloque17," +
  "field_bloque18,field_bloque19,field_bloque20,field_bloque21,field_bloque22,field_bloque23" +
  "&fields[media--image]=field_media_image";

const BASE_DOMAIN = "https://eventos.utpl.edu.ec";

function fixHtmlImages(html) {
  if (!html) return "";
  return html.replace(
    /(<img\s+[^>]*src=["'])(?!https?:\/\/|\/\/)\/?([^"']+)(["'][^>]*>)/gi,
    `$1${BASE_DOMAIN}/$2$3`,
  );
}

function splitPonenteHtml(rawHtml) {
  const html = fixHtmlImages(rawHtml);
  if (!html) return { headerHtml: "", modalHtml: "", buttonText: "Ver perfil" };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;

  const h3 =
    Array.from(body.querySelectorAll("h3")).find((el) =>
      el.textContent.toLowerCase().includes("perfil"),
    ) || body.querySelector("h3");

  if (!h3) {
    return { headerHtml: html, modalHtml: "", buttonText: "Ver perfil" };
  }

  const buttonText = h3.textContent.trim() || "Ver perfil";

  const headerDiv = document.createElement("div");
  const modalDiv = document.createElement("div");

  let isAfterH3 = false;

  Array.from(body.childNodes).forEach((node) => {
    if (node === h3) {
      isAfterH3 = true;
      return;
    }

    if (!isAfterH3) {
      headerDiv.appendChild(node.cloneNode(true));
    } else {
      modalDiv.appendChild(node.cloneNode(true));
    }
  });

  return {
    headerHtml: headerDiv.innerHTML,
    modalHtml: modalDiv.innerHTML,
    buttonText,
  };
}

function PonenteCard({ item, index, onOpenModal }) {
  const { headerHtml, modalHtml, buttonText } = splitPonenteHtml(
    item.processed || item.value || "",
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="
        relative flex flex-col justify-between 
        bg-white rounded-2xl p-6 sm:p-7 
        border border-slate-200/80 shadow-md shadow-slate-200/50 
        hover:shadow-xl hover:border-[#febe10]/60 
        transition-all duration-300
        border-t-4 border-t-[#004270] hover:border-t-[#febe10]
      "
    >
      <div>
        {/* PARTE VISIBLE (Foto, Nombre h2, País) */}
        <div
          className="
            [&_img]:w-full [&_img]:h-52 [&_img]:object-cover [&_img]:rounded-xl [&_img]:mb-4
            [&_h2]:text-lg [&_h2]:sm:text-xl [&_h2]:font-bold [&_h2]:text-[#004270] [&_h2]:mb-2
            [&_p:first-of-type]:inline-flex [&_p:first-of-type]:items-center [&_p:first-of-type]:gap-1.5
            [&_p:first-of-type]:text-xs [&_p:first-of-type]:font-semibold
            [&_p:first-of-type]:text-amber-800 [&_p:first-of-type]:bg-amber-50 [&_p:first-of-type]:border
            [&_p:first-of-type]:border-amber-200 [&_p:first-of-type]:w-fit [&_p:first-of-type]:px-3
            [&_p:first-of-type]:py-1 [&_p:first-of-type]:rounded-md [&_p:first-of-type]:mb-3
          "
          dangerouslySetInnerHTML={{ __html: headerHtml }}
        />
      </div>

      {/* BOTÓN PARA ABRIR EL MODAL */}
      {modalHtml && (
        <button
          type="button"
          onClick={() =>
            onOpenModal({
              headerHtml,
              modalHtml,
              buttonText,
            })
          }
          className="
            mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
            bg-slate-50 hover:bg-amber-50 text-[#004270] hover:text-[#002847]
            border border-slate-200 hover:border-[#febe10] font-semibold text-xs sm:text-sm
            transition-all duration-200 cursor-pointer shadow-sm
          "
        >
          <span>{buttonText}</span>
          <ExternalLink className="w-3.5 h-3.5 text-[#febe10]" />
        </button>
      )}
    </motion.div>
  );
}

function PonenteModal({ ponente, onClose }) {
  // Manejar tecla ESC para cerrar el modal y prevenir scroll de fondo
  useEffect(() => {
    if (!ponente) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [ponente, onClose]);

  if (!ponente) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        {/* Fondo oscuro (Backdrop) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm transition-opacity"
        />

        {/* Ventana modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="
            relative w-full max-w-2xl max-h-[85vh] flex flex-col 
            bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden z-10
          "
        >
          {/* Cabecera del modal */}
          <div className="flex items-start justify-between p-6 border-b border-slate-100 bg-slate-50/70">
            <div
              className="
                [&_img]:hidden
                [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-bold [&_h2]:text-[#004270] [&_h2]:mb-1
                [&_p:first-of-type]:inline-flex [&_p:first-of-type]:items-center [&_p:first-of-type]:text-xs [&_p:first-of-type]:font-semibold [&_p:first-of-type]:text-amber-800 [&_p:first-of-type]:bg-amber-50 [&_p:first-of-type]:border [&_p:first-of-type]:border-amber-200 [&_p:first-of-type]:px-2.5 [&_p:first-of-type]:py-0.5 [&_p:first-of-type]:rounded-md
              "
              dangerouslySetInnerHTML={{ __html: ponente.headerHtml }}
            />
            <button
              type="button"
              onClick={onClose}
              className="
                p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-200/60
                transition-colors cursor-pointer ml-4 flex-shrink-0
              "
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cuerpo del modal con scroll interno */}
          <div
            className="
              p-6 sm:p-8 overflow-y-auto leading-relaxed text-slate-600 text-sm sm:text-base space-y-4
              [&_p]:mb-3 [&_p_strong]:text-[#004270] [&_p_strong]:font-semibold
              [&_a]:text-[#004270] [&_a]:underline [&_a]:decoration-[#febe10] [&_a]:decoration-2
              hover:[&_a]:text-[#005d9c]
            "
            dangerouslySetInnerHTML={{ __html: ponente.modalHtml }}
          />

          {/* Pie del modal */}
          <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="
                px-5 py-2 rounded-xl bg-[#004270] text-white text-sm font-semibold 
                hover:bg-[#002847] transition-colors cursor-pointer shadow-md shadow-[#004270]/20
              "
            >
              Cerrar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function Conferencia() {
  const [selectedPonente, setSelectedPonente] = useState(null);

  const DataDisplay = ({ data }) => {
    const datosBasicos = [
      {
        icon: CalendarDays,
        html: data?.bloque1,
        label: "Fechas",
      },
      {
        icon: Clock,
        html: data?.bloque2,
        label: "Carga académica",
      },
      {
        icon: Laptop,
        html: data?.bloque3,
        label: "Modalidad híbrida",
      },
      {
        icon: MapPin,
        html: data?.bloque4,
        label: "Sede principal",
      },
    ];

    return (
      <div>
        {/* HERO */}
        <section className="relative min-h-[550px] lg:min-h-[640px] flex items-center justify-center overflow-hidden">
          {data?.imagen_portada ? (
            <img
              src={data.imagen_portada}
              alt={data?.titulo || "Portada del evento"}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 bg-[#00365d]" />
          )}

          <div className="absolute inset-0 bg-gradient-to-r from-[#002847]/95 via-[#004270]/85 to-[#00365d]/75" />

          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 w-full text-center sm:text-left">
            <div className="h-1.5 w-20 rounded-full bg-[#febe10] mb-6 mx-auto sm:mx-0" />

            <div
              className="
                [&_p]:inline-flex [&_p]:items-center [&_p]:gap-2 [&_p]:bg-[#febe10] [&_p]:text-[#00365d]
                [&_p]:font-bold [&_p]:text-xs [&_p]:sm:text-sm [&_p]:uppercase [&_p]:tracking-wider
                [&_p]:px-4 [&_p]:py-1.5 [&_p]:rounded-full [&_p]:shadow-md [&_p]:mb-6 [&_p]:w-fit
                [&_h2]:font-extrabold [&_h2]:text-white [&_h2]:leading-tight [&_h2]:tracking-tight
                [&_h2]:max-w-4xl [&_h2]:mb-6 [&_h2_strong]:text-[#febe10] [&_h2]:text-2xl [&_h2]:sm:text-5xl
                [&_h3]:text-xl [&_h3]:text-sky-200 [&_h3]:font-medium [&_h3]:mt-4
                [&_a]:inline-block [&_a]:mt-6 [&_a]:bg-[#febe10] [&_a]:text-[#004270] [&_a]:font-bold
                [&_a]:px-6 [&_a]:py-3 [&_a]:rounded-xl [&_a]:shadow-lg hover:[&_a]:brightness-110 [&_a]:transition
              "
              dangerouslySetInnerHTML={{
                __html: data?.texto_portada || "",
              }}
            />
          </div>
        </section>

        {/* DATOS BASICOS */}
        <section className="relative z-20 -mt-10 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {datosBasicos.map(({ icon: Icon, html }, index) => (
              <div
                key={index}
                className="group relative flex flex-col items-center sm:items-start text-center sm:text-left bg-white rounded-2xl p-6 shadow-lg shadow-slate-200/50 border border-slate-100 hover:border-[#febe10]/60 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-[#004270]/10 text-[#004270] transition-colors duration-300 group-hover:bg-[#004270] group-hover:text-[#febe10]">
                  <Icon className="h-7 w-7 transition-transform duration-300 group-hover:scale-110" />
                </div>

                <div
                  className="
                    w-full
                    [&_h3]:text-xs [&_h3]:uppercase   [&_h3]:text-slate-400 [&_h3]:mb-1.5
                   
                  "
                  dangerouslySetInnerHTML={{
                    __html: html || "",
                  }}
                />

                <div className="absolute bottom-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-transparent to-transparent group-hover:from-[#febe10]/20 group-hover:via-[#febe10] group-hover:to-[#febe10]/20 transition-all duration-300" />
              </div>
            ))}
          </div>
        </section>

        {/* SOBRE EL CONGRESO */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center sm:text-left">
            <div
              className="
                [&_h4]:text-xs [&_h4]:uppercase [&_h4]:tracking-widest [&_h4]:text-[#004270] [&_h4]:bg-[#004270]/10
                [&_h4]:px-3.5 [&_h4]:py-1.5 [&_h4]:rounded-full [&_h4]:inline-block [&_h4]:mb-6
                [&_h3]:inline-flex [&_h3]:items-center [&_h3]:text-sm [&_h3]:text-[#004270] [&_h3]:bg-amber-100
                [&_h3]:border [&_h3]:border-slate-200 [&_h3]:px-4 [&_h3]:py-1.5 [&_h3]:mt-4 [&_h3]:mx-2 [&_h3]:rounded-lg
                [&_h3]:transition-colors hover:[&_h3]:bg-[#febe10]/20 hover:[&_h3]:border-[#febe10]
              "
              dangerouslySetInnerHTML={{
                __html: data?.bloque5 || "",
              }}
            />
          </div>
        </section>

        {/* AVAL */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-50 via-sky-50/40 to-amber-50/30 p-6 sm:p-8 border border-slate-200 shadow-sm border-l-4 border-l-[#febe10]">
              <div className="pointer-events-none absolute -right-6 -bottom-6 text-[#004270]/5">
                <Award size={180} strokeWidth={1} />
              </div>

              <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6">
                <div className="flex-shrink-0 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-[#004270] text-[#febe10] shadow-md shadow-[#004270]/20">
                  <ShieldCheck className="h-8 w-8 sm:h-9 sm:w-9" />
                </div>

                <div
                  className="
                    flex-1 text-center sm:text-left
                [&_h2]:text-2xl  
                    [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-[#004270] [&_h3]:mb-2
                    [&_h4]:text-xs [&_h4]:sm:text-sm [&_h4]:font-bold [&_h4]:uppercase [&_h4]:tracking-wider [&_h4]:text-amber-700 [&_h4]:mb-1.5
                    [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:text-sm [&_p]:sm:text-base [&_p_strong]:text-[#004270] [&_p_strong]:font-semibold
                    [&_a]:text-[#004270] [&_a]:font-semibold [&_a]:underline [&_a]:decoration-[#febe10] [&_a]:decoration-2 [&_a]:underline-offset-4 hover:[&_a]:text-[#005d9c]
                  "
                  dangerouslySetInnerHTML={{
                    __html: data?.bloque6 || "",
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* OBJETIVOS */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="relative rounded-3xl bg-white p-8 sm:p-12 shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#004270] via-[#005d9c] to-[#febe10]" />

              <div className="pointer-events-none absolute -right-8 -bottom-8 text-slate-100/80">
                <Target size={220} strokeWidth={1} />
              </div>

              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#004270] text-[#febe10] shadow-md shadow-[#004270]/20 flex-shrink-0">
                    <Target className="h-6 w-6" />
                  </div>
                </div>

                <div
                  className="  [&_h2]:text-2xl    "
                  dangerouslySetInnerHTML={{
                    __html: data?.bloque7 || "",
                  }}
                />
              </div>
            </div>
          </div>
        </section>
        {/* INVERSIÓN / PRICING */}
        <section
          id="registro"
          className="py-20 bg-white relative overflow-hidden"
        >
          <div className="max-w-6xl mx-auto px-6 sm:px-0">
            {data?.bloque9 && (
              <div
                className="text-center max-w-3xl mx-auto mb-16"
                dangerouslySetInnerHTML={{
                  __html: data.bloque9,
                }}
              />
            )}

            {/* Rejilla de tarjetas de tarifas (bloques 10 al 13) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              {[data?.bloque10, data?.bloque11, data?.bloque12, data?.bloque13]
                .filter(Boolean)
                .map((bloqueHtml, index) => (
                  <div
                    key={index}
                    className="boton1
              group relative flex flex-col justify-between 
              bg-white rounded-3xl p-7 sm:p-8 
              border border-slate-200 shadow-lg shadow-slate-200/40 
              hover:shadow-2xl hover:border-[#febe10] hover:-translate-y-1.5 
              transition-all duration-300
              border-t-4 border-t-[#004270] hover:border-t-[#febe10]

              /* Badges o categorías de la tarifa (h3 / h4) */
              [&_h3]:text-xs [&_h3]:uppercase  [&_h3]:text-center [&_h3]:text-[#004270] [&_h3]:bg-[#004270]/10 [&_h3]:px-3.5 [&_h3]:py-1 [&_h3]:rounded-full [&_h3]:inline-block [&_h3]:mb-3
              [&_h4]:text-sm [&_h4]:font-bold [&_h4]:text-slate-500 [&_h4]:mb-2

              /* Título del plan o tipo de asistente (h2) */
              [&_h2]:text-3xl  
              "
                    dangerouslySetInnerHTML={{
                      __html: bloqueHtml,
                    }}
                  />
                ))}
            </div>
          </div>
        </section>

        {/* SECCIÓN DE PONENTES */}
        {Array.isArray(data?.ponente) && data.ponente.length > 0 && (
          <section className="py-20 bg-slate-50/50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-14">
                <div
                  className="text-center  "
                  dangerouslySetInnerHTML={{
                    __html: data?.bloque8 || "",
                  }}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
                {data.ponente.map((item, index) => (
                  <PonenteCard
                    key={index}
                    item={item}
                    index={index}
                    onOpenModal={setSelectedPonente}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* MODAL GLOBAL DE PONENTE */}
        <PonenteModal
          ponente={selectedPonente}
          onClose={() => setSelectedPonente(null)}
        />

        <div
          className="py-14 max-w-6xl m-auto"
          dangerouslySetInnerHTML={{
            __html: data?.bloque14 || "",
          }}
        />
      </div>
    );
  };

  return <Fetcher url={url}>{(data) => <DataDisplay data={data} />}</Fetcher>;
}

export default Conferencia;
